import Layout from "@/components/Layout";
import { ProductImageThumb } from "@/components/catalog/ProductImageThumb";
import { GenerateGatePassDialog } from "@/components/sales/GenerateGatePassDialog";
import { PaymentReceiptDialog } from "@/components/sales/PaymentReceiptDialog";
import { RecordBalancePaymentDialog } from "@/components/sales/RecordBalancePaymentDialog";
import { RecordInstallmentPaymentDialog, type InstallmentPaymentTarget } from "@/components/sales/RecordInstallmentPaymentDialog";
import { Button } from "@/components/ui/button";
import { buildCategoryProductSchema } from "@/lib/catalog/buildCategorySchema";
import { syncLinksForCategoryFromApi } from "@/lib/catalog/categoryAttributeLinksStore";
import { syncCategoriesFromApi } from "@/lib/catalog/categoriesStore";
import { syncCatalogAttributesFromApi, listCatalogAttributeRecords } from "@/lib/catalog/catalogAttributesStore";
import type { Attribute, AttributeValue } from "@/lib/catalog/types";
import {
  DEAL_TYPE_OPTIONS,
  PROFIT_RULE_OPTIONS,
} from "@/lib/catalog/dealPartners";
import { resolveProductThumbnailUrl } from "@/lib/catalog/productThumbnail";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import { formatDeliveryPayload, nowDeliveryParts, parseDeliveryParts } from "@/lib/sales/gatePass";
import {
  fetchSaleInvoice,
  cancelSale,
  paySaleBalance,
  paySaleInstallment,
  saleInvoiceErrorMessage,
  updateSaleDelivery,
  type ProfitDistributionSummary,
  type SaleInstallmentRecord,
  type SaleInvoiceRecord,
  type SalePaymentRecord,
  type SalePaymentStatus,
} from "@/lib/sales/saleInvoiceStore";
import { installmentRemaining, installmentStatusClass, installmentStatusLabel } from "@/lib/sales/saleInstallments";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { format, isValid, parseISO } from "date-fns";
import {
  ArrowLeft,
  Ban,
  Car,
  ChevronRight,
  Download,
  Edit,
  ExternalLink,
  FileText,
  HandCoins,
  Handshake,
  History,
  Loader2,
  Phone,
  Plus,
  Printer,
  Receipt,
  Search,
  ShieldCheck,
  Store,
  Ticket,
  TrendingUp,
  User,
  UserCheck,
  Users,
  Wallet,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/* ─── Formatting helpers ──────────────────────────────────────────── */

function formatAmount(n: number): string {
  return n.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function MoneyAmount({
  value,
  className,
  amountClassName,
  emphasis = false,
  showShort = false,
}: {
  value: number | null | undefined;
  className?: string;
  amountClassName?: string;
  emphasis?: boolean;
  showShort?: boolean;
}) {
  if (value == null || !Number.isFinite(Number(value))) {
    return <span className={cn("text-muted-foreground", className)}>—</span>;
  }

  const num = Number(value);
  const short = showShort ? pkrAmountShortLabel(num) : "";

  return (
    <span className={cn("inline-flex flex-col items-end tabular-nums", className)}>
      <span className={cn(emphasis ? "font-semibold text-foreground" : "font-medium text-foreground", amountClassName)}>
        {formatCurrency(num)}
      </span>
      {short ? <span className="text-[10px] text-muted-foreground">{short}</span> : null}
    </span>
  );
}

function paymentMethodLabel(method: string | null | undefined): string {
  if (method === "cash") return "Cash";
  if (method === "online") return "Online";
  if (method === "pay_order") return "Pay Order";
  if (method === "bank") return "Bank";
  if (method === "cheque") return "Cheque";
  return method ?? "—";
}

function tokenTypeLabel(type: string | null | undefined): string {
  if (type === "conditional") return "Conditional";
  if (type === "unconditional") return "Un-Conditional";
  return type ?? "—";
}

function vehicleStatusBadge(sale: SaleInvoiceRecord): { label: string; className: string } {
  if (sale.is_returned || sale.void_type === "return" || sale.payment_status === "returned") {
    return {
      label: "Returned",
      className: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
    };
  }
  if (sale.is_cancelled || sale.cancelled_at || sale.payment_status === "cancelled") {
    return {
      label: "Cancelled",
      className: "bg-slate-500/15 text-slate-700 dark:text-slate-300",
    };
  }

  const status = (sale.product?.inventory_status ?? "sold").toLowerCase();
  if (status === "in_stock") {
    return {
      label: "In Stock",
      className: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
    };
  }
  if (status === "reserved") {
    return {
      label: "Reserved",
      className: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    };
  }
  return {
    label: "Sold",
    className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  };
}

function PaymentStatusBadge({ status }: { status: SalePaymentStatus | null | undefined }) {
  if (!status) return null;
  const label =
    status === "paid"
      ? "Fully paid"
      : status === "partial"
        ? "Partially paid"
        : status === "returned"
          ? "Returned"
          : status === "cancelled"
            ? "Cancelled"
            : "Unpaid";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
        status === "paid"
          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30"
          : status === "partial"
            ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30"
            : status === "returned"
              ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/30"
              : status === "cancelled"
                ? "bg-slate-500/15 text-slate-700 dark:text-slate-300 border border-slate-500/30"
                : "bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/30",
      )}
    >
      {status === "paid" ? <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> : null}
      {label}
    </span>
  );
}

function optionLabel(options: { value: string; label: string }[], value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  return options.find((o) => o.value === value)?.label ?? value;
}

function formatRatio(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `${Number(n).toFixed(2)}%`;
}

/** Net-profit rules show profit share (`amount`); owner surplus/commission show payout amounts. */
function usesNetProfitShares(summary: ProfitDistributionSummary | null | undefined): boolean {
  const deal = summary?.deal_type;
  const rule = summary?.profit_rule_type;
  if (deal === "purchased") return true;
  if (deal === "partnership" && (rule === "commission" || rule === "profit_sharing")) return true;
  if (deal === "owner_vehicle" && rule === "profit_sharing") return true;
  return false;
}

function isOwnerSurplusSummary(summary: ProfitDistributionSummary | null | undefined): boolean {
  return summary?.deal_type === "owner_vehicle" && summary?.profit_rule_type === "owner_surplus";
}

function recipientShareAmount(
  row: {
    amount?: number | null;
    final_payable?: number | null;
  },
  summary: ProfitDistributionSummary | null | undefined,
): number | null {
  const profit = row.amount != null ? Number(row.amount) : null;
  const payable = row.final_payable != null ? Number(row.final_payable) : null;

  if (isOwnerSurplusSummary(summary)) {
    if (payable != null && Number.isFinite(payable)) return payable;
  } else if (usesNetProfitShares(summary)) {
    if (profit != null && Number.isFinite(profit)) return profit;
  } else {
    if (profit != null && Number.isFinite(profit) && profit > 0) return profit;
    if (payable != null && Number.isFinite(payable)) return payable;
  }

  if (payable != null && Number.isFinite(payable)) return payable;
  return profit;
}

/** Generate synthetic receipt ID from invoice number and payment index. */
function makeReceiptId(invoiceNo: string, index: number): string {
  const suffix = String.fromCharCode(65 + index); // A, B, C, ...
  return `REC-${invoiceNo}-${suffix}`;
}

/** Map payment method to a styled badge config. */
function paymentMethodBadge(method: string | null | undefined): { label: string; emoji: string; className: string } {
  if (method === "cash") return { label: "Cash", emoji: "💵", className: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/60" };
  if (method === "online") return { label: "Online IBFT", emoji: "🌐", className: "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300 border-sky-200 dark:border-sky-800/60" };
  if (method === "cheque") return { label: "Cheque", emoji: "🏛", className: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200 dark:border-amber-800/60" };
  if (method === "pay_order") return { label: "Pay Order", emoji: "🏦", className: "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/60" };
  if (method === "bank") return { label: "Bank", emoji: "🏦", className: "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300 border-sky-200 dark:border-sky-800/60" };
  return { label: method ?? "—", emoji: "💳", className: "bg-muted/50 text-muted-foreground border-border/50" };
}

/* ─── Sub-components ──────────────────────────────────────────────── */

function SummaryRow({
  label,
  value,
  emphasis = false,
  muted = false,
  borderTop = false,
}: {
  label: string;
  value: ReactNode;
  emphasis?: boolean;
  muted?: boolean;
  borderTop?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 items-start justify-between gap-2 py-1.5 text-sm",
        borderTop && "mt-0.5 border-t border-border/40 pt-2",
      )}
    >
      <span
        className={cn(
          "min-w-0 flex-1 break-words leading-snug text-muted-foreground",
          emphasis && "font-medium text-foreground",
        )}
      >
        {label}
      </span>
      <span className={cn("max-w-[58%] shrink-0 text-right leading-snug", muted && "text-muted-foreground")}>
        {value}
      </span>
    </div>
  );
}

function MetaBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-border/50 bg-background px-2 py-0.5 text-[11px] font-medium text-foreground">
      {children}
    </span>
  );
}

function SectionCard({
  title,
  subtitle,
  badge,
  icon: Icon,
  children,
  action,
  className,
  bodyClassName,
}: {
  title: string;
  subtitle?: ReactNode;
  badge?: ReactNode;
  icon?: typeof Wallet;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn("glass-card h-full flex flex-col overflow-hidden", className)}>
      <div className="flex flex-col gap-2.5 border-b border-border/30 bg-muted/15 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4 lg:px-5 lg:py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {Icon ? (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
              <Icon className="h-4 w-4" />
            </div>
          ) : null}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="whitespace-nowrap text-sm font-bold text-foreground lg:text-base">{title}</h2>
              {badge}
            </div>
            {subtitle ? (
              <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
            ) : null}
          </div>
        </div>
        {action ? <div className="min-w-0 w-full sm:w-auto shrink-0">{action}</div> : null}
      </div>
      <div className={cn("min-w-0 p-3 sm:p-4 lg:p-5 flex-1 flex flex-col justify-between", bodyClassName)}>{children}</div>
    </section>
  );
}

function RecipientCard({
  name,
  percent,
  amount,
  note,
  emphasis = false,
  payable,
  paid,
  balance,
  index,
}: {
  name: string;
  percent?: number | null;
  amount?: number | null;
  note?: string | null;
  emphasis?: boolean;
  payable?: number | null;
  paid?: number | null;
  balance?: number | null;
  index?: number;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2.5",
        emphasis ? "border-primary/25 bg-primary/[0.05]" : "border-border/40 bg-background/70",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          {index != null ? (
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary mt-0.5">
              {index}
            </span>
          ) : null}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{name}</p>
            {note ? <p className="mt-0.5 text-[11px] text-muted-foreground">{note}</p> : null}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[11px] font-medium tabular-nums text-muted-foreground">{formatRatio(percent)}</p>
          <MoneyAmount value={amount} emphasis />
        </div>
      </div>

      {payable != null && (payable > 0 || (paid ?? 0) > 0 || (balance ?? 0) > 0) ? (
        <div className="mt-2.5 grid grid-cols-3 gap-2 border-t border-border/25 pt-2 text-[10px] text-muted-foreground leading-normal">
          <div>
            <span className="block text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80">Payable</span>
            <MoneyAmount value={payable} />
          </div>
          <div>
            <span className="block text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80">Paid</span>
            <MoneyAmount value={paid} amountClassName="text-primary" />
          </div>
          <div>
            <span className="block text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80">Balance</span>
            <MoneyAmount value={balance} emphasis />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────────────────── */

const SaleInvoice = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [sale, setSale] = useState<SaleInvoiceRecord | null>(null);
  const [paymentTarget, setPaymentTarget] = useState<InstallmentPaymentTarget | null>(null);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [downPaymentDialogOpen, setDownPaymentDialogOpen] = useState(false);
  const [gatePassOpen, setGatePassOpen] = useState(false);
  const [gatePassSaving, setGatePassSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSaving, setCancelSaving] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [selectedReceiptPayment, setSelectedReceiptPayment] = useState<SalePaymentRecord | null>(null);
  const [selectedReceiptId, setSelectedReceiptId] = useState("");
  const [extraProfitDetailsOpen, setExtraProfitDetailsOpen] = useState(false);
  const [gatePassDate, setGatePassDate] = useState(() => nowDeliveryParts().date);
  const [gatePassTime, setGatePassTime] = useState(() => nowDeliveryParts().time);
  const [checklistValues, setChecklistValues] = useState<Record<string, AttributeValue>>({});
  const [dataLoaded, setDataLoaded] = useState(false);

  // Payment ledger filters
  const [ledgerSearch, setLedgerSearch] = useState("");
  const [ledgerMethodFilter, setLedgerMethodFilter] = useState<string>("all");

  const canRecordPayment = can(getStoredAuthUser(), "sale_can_update") || can(getStoredAuthUser(), "sale_can_create");
  const canGenerateGatePass = canRecordPayment;
  const canCancelSale = canRecordPayment;
  const canEditSale = can(getStoredAuthUser(), "sale_can_update") || can(getStoredAuthUser(), "sale_can_create");

  const checklistAttributes = useMemo(() => {
    const all = listCatalogAttributeRecords();
    return all.filter((a) => a.isDeliveryChecklist);
  }, [dataLoaded]);

  const reload = () => {
    if (!id) return Promise.resolve();
    return fetchSaleInvoice(id).then((r) => setSale(r));
  };

  useEffect(() => {
    if (!id) {
      setSale(null);
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);

    // Fetch essential catalog metadata for checklist resolution
    Promise.all([
      syncCategoriesFromApi(),
      syncCatalogAttributesFromApi(),
    ])
      .then(async () => {
        const r = await fetchSaleInvoice(id);
        if (mounted && r) {
          setSale(r);
          if (r.product?.category_id) {
            await syncLinksForCategoryFromApi(r.product.category_id);
          }
          setDataLoaded(true);
        }
      })
      .catch((e) => {
        toast.error(saleInvoiceErrorMessage(e));
        if (mounted) setSale(null);
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [id]);

  const invoiceNo = useMemo(() => (sale ? sale.invoice_no || String(sale.id) : ""), [sale?.invoice_no, sale?.id]);

  const installmentOutstanding = useMemo(() => {
    if (sale?.installment_outstanding != null) return sale.installment_outstanding;
    if (!sale?.installments?.length) return null;
    return sale.installments.reduce((sum, row) => sum + installmentRemaining(row), 0);
  }, [sale?.installment_outstanding, sale?.installments]);

  const installmentStats = useMemo(() => {
    const rows = sale?.installments ?? [];
    if (!rows.length) return null;
    const paidCount = rows.filter((r) => r.status === "paid").length;
    return { total: rows.length, paidCount };
  }, [sale?.installments]);

  const distributions = sale?.profit_distributions ?? [];
  const summary = sale?.profit_distribution_summary ?? null;
  const summaryPartners = summary?.partners ?? [];
  const netProfitValue = Number(summary?.net_profit ?? 0);
  const distributionTotalValue = Number(summary?.distribution_total ?? netProfitValue);
  const hasSummaryRecipients =
    Boolean(summary?.showroom) ||
    summaryPartners.length > 0 ||
    distributions.length > 0;

  const balanceAmount = sale?.balance_amount ?? sale?.total_amount ?? 0;
  const initialPaymentIsFull =
    Number(sale?.total_amount ?? 0) > 0 &&
    Number(sale?.token_amount ?? 0) >= Number(sale?.total_amount ?? 0) - 0.009;
  const initialPaymentReference =
    sale?.transaction_reference_no || sale?.pay_order_no || null;
  const productImageUrl = useMemo(() => resolveProductThumbnailUrl(sale?.product ?? null), [sale?.product]);

  // Filtered payments for the ledger
  const filteredPayments = useMemo(() => {
    if (!sale?.payments) return [];
    let filtered = sale.payments;

    // Method filter
    if (ledgerMethodFilter !== "all") {
      filtered = filtered.filter((p) => p.payment_method === ledgerMethodFilter);
    }

    // Search filter
    if (ledgerSearch.trim()) {
      const q = ledgerSearch.toLowerCase().trim();
      filtered = filtered.filter((p) => {
        const haystack = [
          p.bank_name,
          p.transaction_reference_no,
          p.pay_order_no,
          p.notes,
          p.transaction_type_label ?? p.transaction_type,
          p.payment_method_label ?? paymentMethodLabel(p.payment_method),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    return filtered;
  }, [sale?.payments, ledgerMethodFilter, ledgerSearch]);

  // Unique payment methods for filter pills
  const paymentMethods = useMemo(() => {
    if (!sale?.payments?.length) return [];
    const methods = new Set(sale.payments.map((p) => p.payment_method).filter(Boolean));
    return Array.from(methods) as string[];
  }, [sale?.payments]);

  if (!id) {
    return (
      <Layout title="Invoice">
        <div className="glass-card p-8 text-sm text-muted-foreground">Missing sale id.</div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Invoice">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading invoice…
        </div>
      </Layout>
    );
  }

  if (!sale) {
    return (
      <Layout title="Invoice not found">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Invoice not found.</div>
          <Link to="/product-sales">
            <Button variant="glass" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to sales
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const customerMeta = [sale.customer?.phone ? `📞 ${sale.customer.phone}` : null, sale.customer?.city ? `City: ${sale.customer.city}` : null]
    .filter(Boolean)
    .join(" · ");

  const dealTypeLabel = optionLabel(DEAL_TYPE_OPTIONS, sale.product?.deal_type);
  const profitRuleLabel =
    sale.product?.deal_type === "purchased" && !sale.product?.profit_rule_type
      ? "Not applicable"
      : optionLabel(PROFIT_RULE_OPTIONS, sale.product?.profit_rule_type);

  const openGatePassModal = () => {
    const parts = parseDeliveryParts(sale?.delivery_date);
    setGatePassDate(parts.date);
    setGatePassTime(parts.time);

    // Populate checklist attributes initial values
    const initialValues: Record<string, AttributeValue> = {};
    if (sale?.product?.attributes) {
      for (const item of sale.product.attributes) {
        initialValues[item.code] = item.value;
      }
    }
    setChecklistValues(initialValues);

    setGatePassOpen(true);
  };

  const submitGatePass = async () => {
    const payload = formatDeliveryPayload(gatePassDate, gatePassTime);
    if (!payload) {
      toast.error("Delivery date and time are required.");
      return;
    }
    setGatePassSaving(true);
    try {
      const r = await updateSaleDelivery(sale.id, payload, checklistValues);
      if (r.record) {
        setSale(r.record);
      } else {
        await reload();
      }
      toast.success(r.message);
      setGatePassOpen(false);
    } catch (e) {
      toast.error(saleInvoiceErrorMessage(e));
    } finally {
      setGatePassSaving(false);
    }
  };

  const submitCancelSale = async () => {
    if (!sale) return;
    setCancelSaving(true);
    try {
      const r = await cancelSale(sale.id, {
        notes: "Customer walk-away — sale cancelled",
        payment_method: sale.payment_method ?? "cash",
      });
      if (r.record) {
        setSale(r.record);
      } else {
        await reload();
      }
      toast.success(r.message);
      setCancelOpen(false);
    } catch (e) {
      toast.error(saleInvoiceErrorMessage(e));
    } finally {
      setCancelSaving(false);
    }
  };

  const isReturned = Boolean(sale.is_returned || sale.void_type === "return" || sale.payment_status === "returned");
  const isCancelled = Boolean(
    sale.is_cancelled || sale.cancelled_at || sale.payment_status === "cancelled" || isReturned,
  );
  const canShowEdit =
    canEditSale &&
    !isCancelled &&
    !isReturned &&
    !sale.is_installment;
  const isFullyPaid = sale.payment_status === "paid"
    || (
      Number(sale.outstanding ?? sale.balance_outstanding ?? 1) <= 0.009
      && Number(sale.paid_total ?? 0) > 0.009
    );
  const paidTotalAmount = Number(sale.paid_total ?? 0);
  const totalSaleAmount = Number(sale.total_amount ?? 0);
  const percentPaid = totalSaleAmount > 0 ? Math.min(100, Math.max(0, Math.round((paidTotalAmount / totalSaleAmount) * 100))) : 0;

  const currSymbol = getActiveCompanyCurrency().symbol;

  return (
    <Layout title={`Invoice ${invoiceNo}`}>
      <div className="w-full space-y-2 lg:space-y-2">

        {/* ── 1. HEADER BAR ───────────────────────────────────────── */}
        <div className="glass-card flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between lg:gap-3">
          <div className="min-w-0 space-y-1 lg:flex-1">
            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
                <FileText className="h-5 w-5" />
              </div>
              <h1 className="font-display text-xl font-bold text-foreground sm:text-2xl">
                Invoice #{invoiceNo}
              </h1>
              <PaymentStatusBadge status={sale.payment_status} />
              <span className="rounded-md border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs font-semibold text-foreground">
                {sale.is_installment ? "Installment Sale" : sale.token_amount ? "Token Payment" : "Full Payment"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground pl-0 sm:pl-13">
              Created on {sale.date && isValid(parseISO(sale.date)) ? format(parseISO(sale.date), "MMMM do, yyyy") : (sale.date ?? "—")}
            </p>
          </div>

          <div className="flex flex-nowrap items-center gap-2 shrink-0 overflow-x-auto">
            {(() => {
              // For installment sales, gate pass is eligible once down payment is fully received.
              // For non-installment sales, gate pass requires full payment ("paid" status).
              const gatePassEligible = sale.is_installment
                ? (sale.down_payment_outstanding ?? 0) <= 0.009
                : sale.payment_status === "paid";

              return (
                <>
                  {canGenerateGatePass && !isCancelled && !gatePassEligible ? (
                    <span
                      className="inline-flex shrink-0 items-center gap-1.5 h-9 whitespace-nowrap px-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300 text-xs font-semibold"
                      title={
                        sale.is_installment
                          ? `Gate pass requires down payment clearance. Remaining: ${formatCurrency(sale.down_payment_outstanding ?? 0)}`
                          : `Gate pass requires full payment settlement. Remaining balance: ${formatCurrency(balanceAmount)}`
                      }
                    >
                      <Ticket className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                      {sale.is_installment ? "Gate Pass: Pending Down Payment" : "Gate Pass: Pending Clearance"}
                    </span>
                  ) : null}
                  {canGenerateGatePass && !isCancelled && gatePassEligible && !sale.delivery_date ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 gap-1.5 h-9 whitespace-nowrap text-xs border-border/60 bg-background text-foreground hover:bg-muted"
                      onClick={openGatePassModal}
                    >
                      <Ticket className="h-3.5 w-3.5" />
                      Generate Gate Pass
                    </Button>
                  ) : null}
                  {!isCancelled && gatePassEligible && sale.delivery_date ? (
                    <>
                      <a href={`/sales/${sale.id}/gate-pass/pdf`} target="_blank" rel="noreferrer" className="inline-flex shrink-0">
                        <Button type="button" variant="outline" size="sm" className="gap-1.5 h-9 whitespace-nowrap text-xs border-border/60 bg-background text-foreground hover:bg-muted">
                          <Printer className="h-3.5 w-3.5" />
                          View Gate Pass
                        </Button>
                      </a>
                      {canGenerateGatePass ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="shrink-0 gap-1.5 h-9 whitespace-nowrap text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                          onClick={openGatePassModal}
                        >
                          <Ticket className="h-3.5 w-3.5" />
                          Update
                        </Button>
                      ) : null}
                    </>
                  ) : null}
                </>
              );
            })()}
            {canRecordPayment && !isCancelled && sale.is_installment && (sale.down_payment_outstanding ?? 0) > 0 ? (
              <Button
                type="button"
                variant="premium"
                size="sm"
                className="shrink-0 gap-1.5 h-9 whitespace-nowrap text-xs font-semibold shadow-xs"
                onClick={() => setDownPaymentDialogOpen(true)}
              >
                <Wallet className="h-3.5 w-3.5" />
                Pay Down Payment
              </Button>
            ) : null}
            {canRecordPayment && !isCancelled && !sale.is_installment && (sale.balance_outstanding ?? 0) > 0 ? (
              <Button
                type="button"
                variant="premium"
                size="sm"
                className="shrink-0 gap-1.5 h-9 whitespace-nowrap text-xs font-semibold shadow-xs"
                onClick={() => setBalanceDialogOpen(true)}
              >
                <Wallet className="h-3.5 w-3.5" />
                Record Payment
              </Button>
            ) : null}
            <a href={`/sales/${sale.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex shrink-0">
              <Button variant="default" size="sm" className="gap-1.5 h-9 whitespace-nowrap text-xs px-4 font-semibold bg-sky-600 hover:bg-sky-700 text-white shadow-xs">
                <Download className="h-3.5 w-3.5" />
                Download PDF
              </Button>
            </a>
            {canShowEdit ? (
              <Link to={`/product-sales/${sale.id}/edit`} className="inline-flex shrink-0">
                <Button type="button" variant="outline" size="sm" className="gap-1.5 h-9 whitespace-nowrap text-xs border-border/60 bg-background text-foreground hover:bg-muted">
                  <Edit className="h-3.5 w-3.5" />
                  Edit sale
                </Button>
              </Link>
            ) : null}
            {canCancelSale && !isCancelled && !isFullyPaid ? (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="shrink-0 h-9 gap-1.5 whitespace-nowrap px-3.5 text-xs font-semibold"
                onClick={() => setCancelOpen(true)}
              >
                <Ban className="h-3.5 w-3.5" />
                Cancel sale
              </Button>
            ) : null}
          </div>
        </div>

        {/* ── 2. COLLECTION PROGRESS BANNER ────────────────────────────── */}
        <div className="glass-card overflow-hidden">
          <div className="px-4 py-4 sm:px-5 lg:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/25 bg-primary/10 text-primary">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Collection Progress
                  </p>
                  <div className="mt-0.5 flex items-baseline gap-2">
                    <span className="text-lg font-bold text-foreground tabular-nums">
                      {currSymbol} {formatAmount(paidTotalAmount)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      of {currSymbol} {formatAmount(totalSaleAmount)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5 sm:text-right">
                <div className="flex items-center justify-between gap-6 text-xs font-semibold sm:justify-end">
                  <span className="text-muted-foreground">Payment Status</span>
                  <span className={cn(percentPaid >= 100 ? "text-emerald-600 dark:text-emerald-400 font-bold" : "text-primary")}>
                    {percentPaid}% Paid
                  </span>
                </div>
                <div className="h-2.5 w-full sm:w-64 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      "h-full transition-all duration-700 rounded-full",
                      percentPaid >= 100
                        ? "bg-emerald-500"
                        : percentPaid > 0
                          ? "bg-gradient-to-r from-primary/70 to-primary"
                          : "bg-muted-foreground/30",
                    )}
                    style={{ width: `${percentPaid}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── 3. TWO-COLUMN: SALE OVERVIEW + PAYMENT & DEAL FINANCIALS ── */}
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          {/* Left — Sale Overview */}
          <SectionCard
            title="Sale Overview"
            icon={FileText}
            action={
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-muted/40 px-2.5 py-1 text-xs font-mono font-bold text-foreground shadow-2xs">
                Ref #{invoiceNo}
              </span>
            }
          >
            <div className="space-y-3.5">
              {/* Customer & Salesperson Cards */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Customer Card */}
                <div className="relative overflow-hidden rounded-xl border border-border/50 bg-card/60 p-3.5 shadow-2xs hover:border-border transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-blue-500/20 bg-blue-500/10 text-blue-600 dark:text-blue-400">
                      <User className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Customer
                      </p>
                      <p className="mt-0.5 truncate text-sm font-bold text-foreground">
                        {sale.customer?.name ?? "Walk-in Customer"}
                      </p>
                      {sale.customer?.phone ? (
                        <a
                          href={`tel:${sale.customer.phone}`}
                          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                        >
                          <Phone className="h-3 w-3 shrink-0" />
                          <span>{sale.customer.phone}</span>
                        </a>
                      ) : (
                        <p className="mt-1 text-[11px] text-muted-foreground">No phone provided</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Salesperson Card */}
                <div className="relative overflow-hidden rounded-xl border border-border/50 bg-card/60 p-3.5 shadow-2xs hover:border-border transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-purple-500/20 bg-purple-500/10 text-purple-600 dark:text-purple-400">
                      <UserCheck className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Salesperson
                      </p>
                      {sale.employee?.full_name ? (
                        <>
                          <p className="mt-0.5 truncate text-sm font-bold text-foreground">
                            {sale.employee.full_name}
                          </p>
                          {sale.employee.employee_code ? (
                            <p className="mt-1 text-[11px] font-medium text-muted-foreground">
                              Code: <span className="font-mono text-foreground/80">{sale.employee.employee_code}</span>
                            </p>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <p className="mt-0.5 text-sm font-medium text-foreground/80">
                            Direct Showroom Sale
                          </p>
                          <p className="mt-1 text-[11px] text-muted-foreground">No agent assigned</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {sale.description?.trim() ? (
                <div className="rounded-xl border border-border/40 bg-muted/20 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Notes &amp; Remarks
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-xs text-foreground/90 leading-relaxed">{sale.description}</p>
                </div>
              ) : null}

              {/* Selected Vehicle Card */}
              <div className="relative overflow-hidden rounded-xl border border-border/60 bg-gradient-to-br from-card to-muted/20 p-3.5 shadow-2xs hover:border-primary/40 transition-colors">
                <div className="flex items-center gap-3.5">
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded-lg border border-border/60 bg-muted shadow-2xs relative group">
                    <ProductImageThumb
                      src={productImageUrl}
                      alt={sale.product?.name ?? "Product"}
                      iconClassName="h-7 w-7"
                    />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-primary flex items-center gap-1">
                        <Car className="h-3 w-3" />
                        Vehicle
                      </span>
                      {(() => {
                        const badge = vehicleStatusBadge(sale);
                        return (
                          <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold border", badge.className)}>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </div>
                    {sale.product?.id ? (
                      <Link
                        to={`/products/${sale.product.id}`}
                        className="group/link flex items-center gap-1.5 truncate text-sm font-bold text-foreground hover:text-primary transition-colors"
                      >
                        <span className="truncate">{sale.product?.name ?? "—"}</span>
                        <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity text-primary" />
                      </Link>
                    ) : (
                      <p className="truncate text-sm font-bold text-foreground">
                        {sale.product?.name ?? "—"}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right pl-3 border-l border-border/40">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Sale Total</p>
                    <MoneyAmount value={sale.total_amount} emphasis showShort amountClassName="text-base font-extrabold" />
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Right — Payment & Deal Financials */}
          <SectionCard
            title="Payment & Deal Financials"
            icon={Wallet}
            action={
              <div className="flex flex-wrap items-center gap-1.5 justify-end">
                <PaymentStatusBadge status={sale.payment_status} />
                {sale.token_amount != null && Number(sale.token_amount) > 0 ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                    ✓ Token Verified
                  </span>
                ) : null}
              </div>
            }
          >
            <div className="space-y-1.5">
              <SummaryRow label="Total Sale Price" value={<MoneyAmount value={sale.total_amount} emphasis />} />

              {/* Booking Token line */}
              {sale.token_amount != null && Number(sale.token_amount) > 0 ? (
                <div className="my-1.5 flex min-w-0 items-center justify-between gap-3 rounded-xl border border-border/50 bg-muted/20 px-3.5 py-2.5">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-foreground">Booking Token</span>
                    <span className="rounded-md border border-border/60 bg-background/80 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      {tokenTypeLabel(sale.token_type)}
                    </span>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="text-sm font-bold text-foreground tabular-nums">
                      {formatCurrency(Number(sale.token_amount))}
                    </span>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {paymentMethodLabel(sale.payment_method)}
                      {sale.date && isValid(parseISO(sale.date)) ? ` • ${format(parseISO(sale.date), "MMM d, yyyy")}` : ""}
                    </p>
                  </div>
                </div>
              ) : null}

              {/* For Installment Sales: Down Payment card */}
              {sale.is_installment ? (
                <div className="my-2 rounded-xl border border-border/60 bg-muted/20 p-3 space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-foreground">Agreed Down Payment</span>
                    <MoneyAmount value={sale.down_payment ?? 0} emphasis />
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Down Payment Paid Realized</span>
                    <MoneyAmount
                      value={sale.down_payment_paid ?? (sale.token_amount ?? 0)}
                      amountClassName="text-emerald-600 dark:text-emerald-400 font-semibold"
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs pt-1.5 border-t border-border/40">
                    <span className="font-semibold text-foreground">Remaining Down Payment</span>
                    <MoneyAmount
                      value={sale.down_payment_outstanding ?? 0}
                      emphasis
                      amountClassName={
                        Number(sale.down_payment_outstanding ?? 0) <= 0.009
                          ? "text-emerald-600 dark:text-emerald-400 font-bold"
                          : "text-amber-600 dark:text-amber-400 font-bold"
                      }
                    />
                  </div>
                </div>
              ) : (
                <SummaryRow label="Balance Payable" value={<MoneyAmount value={balanceAmount} emphasis />} />
              )}

              {/* Balance paid total */}
              {!sale.is_installment && sale.balance_paid_total != null && Number(sale.balance_paid_total) > 0 ? (
                <SummaryRow
                  label="Balance Paid Realized"
                  value={<MoneyAmount value={sale.balance_paid_total} amountClassName="text-emerald-600 dark:text-emerald-400 font-semibold" />}
                />
              ) : null}

              {/* Installment totals */}
              {sale.is_installment && installmentOutstanding != null ? (
                <>
                  <SummaryRow
                    label="Financed Principal (Installments)"
                    value={<MoneyAmount value={sale.balance_amount ?? 0} emphasis />}
                  />
                  <SummaryRow
                    label="Installments Paid Total"
                    value={<MoneyAmount value={sale.installment_paid_total ?? 0} />}
                  />
                  <SummaryRow
                    label="Installments Outstanding"
                    value={<MoneyAmount value={installmentOutstanding} emphasis />}
                    emphasis
                  />
                </>
              ) : null}

              {/* Remaining balance due */}
              <div className="pt-1.5">
                <div className="flex min-w-0 items-start justify-between gap-2 py-1 text-sm">
                  <div className="min-w-0 flex-1">
                    <span className="font-bold text-foreground leading-snug">
                      {sale.is_installment ? "Total Remaining Due" : "Remaining Balance Due"}
                    </span>
                    {sale.payable_date ? (
                      <p className="mt-0.5 text-[11px] text-muted-foreground">Due Date: {sale.payable_date}</p>
                    ) : null}
                  </div>
                  <span className="shrink-0 text-right">
                    <MoneyAmount
                      value={sale.outstanding ?? (sale.is_installment ? installmentOutstanding : (sale.balance_outstanding ?? 0))}
                      emphasis
                      amountClassName={
                        Number(sale.outstanding ?? (sale.is_installment ? installmentOutstanding : (sale.balance_outstanding ?? 0))) <= 0.009
                          ? "text-emerald-600 dark:text-emerald-400 font-bold text-base"
                          : "text-foreground font-bold text-base"
                      }
                    />
                  </span>
                </div>
              </div>

              {/* Record Payment button */}
              {canRecordPayment && !isCancelled && !sale.is_installment && (sale.balance_outstanding ?? 0) > 0 ? (
                <Button
                  type="button"
                  variant="premium"
                  size="sm"
                  className="mt-3 w-full gap-2 h-10 shadow-xs text-xs font-semibold"
                  onClick={() => setBalanceDialogOpen(true)}
                >
                  <Wallet className="h-4 w-4" />
                  Record Balance Payment
                </Button>
              ) : null}

              {canRecordPayment && !isCancelled && sale.is_installment && (sale.down_payment_outstanding ?? 0) > 0 ? (
                <Button
                  type="button"
                  variant="premium"
                  size="sm"
                  className="mt-3 w-full gap-2 h-10 shadow-xs text-xs font-semibold"
                  onClick={() => setDownPaymentDialogOpen(true)}
                >
                  <Wallet className="h-4 w-4" />
                  Record Remaining Down Payment ({formatCurrency(sale.down_payment_outstanding ?? 0)})
                </Button>
              ) : null}
            </div>
          </SectionCard>
        </div>

        {/* ── 4. PROFIT & COMMISSION DISTRIBUTION ──────────────────────── */}
        <SectionCard
          title="Profit & Commission Distribution"
          icon={Handshake}
          action={
            <div className="flex flex-wrap items-center justify-start gap-2 sm:justify-end">
              {summaryPartners.length > 0 ? (
                <span className="rounded-full border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs font-semibold text-foreground">
                  {summaryPartners.length} Partner{summaryPartners.length > 1 ? "s" : ""}
                </span>
              ) : null}
              <span className="rounded-lg border border-border/70 bg-muted/40 px-3 py-1 text-xs font-medium text-foreground">
                Deal – {dealTypeLabel}
              </span>
              <span className="rounded-lg border border-border/70 bg-muted/40 px-3 py-1 text-xs font-medium text-foreground">
                Rule – {profitRuleLabel}
              </span>
            </div>
          }
        >
          {!hasSummaryRecipients ? (
            <div className="rounded-lg border border-dashed border-border/60 bg-muted/10 px-3 py-6 text-center">
              <p className="text-sm font-medium text-foreground">No profit distribution recorded</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Updates automatically from deal type, profit rule, sale price, costs, and partners.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {isOwnerSurplusSummary(summary) ? (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:gap-4 xl:grid-cols-3">
                  {summary?.owner_payable ? (
                    <div className="min-w-0 rounded-lg border border-border/40 bg-muted/10 px-3 py-0.5">
                      <p className="py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Owner payable
                      </p>
                      <SummaryRow
                        label="Owner demand price"
                        value={<MoneyAmount value={summary.owner_payable.owner_demand_price} emphasis />}
                      />
                      <SummaryRow
                        label="Total owner payable"
                        value={<MoneyAmount value={summary.owner_payable.total_payable} emphasis />}
                        borderTop
                        emphasis
                      />
                    </div>
                  ) : null}
                  {summary?.surplus_distribution ? (
                    <div className="min-w-0 rounded-lg border border-border/40 bg-muted/10 px-3 py-0.5">
                      <p className="py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Surplus
                      </p>
                      <SummaryRow
                        label="Sale − owner demand"
                        value={<MoneyAmount value={summary.surplus_distribution.surplus} emphasis />}
                      />
                      <SummaryRow
                        label={`Showroom (${formatRatio(summary.surplus_distribution.showroom_percent)})`}
                        value={<MoneyAmount value={summary.surplus_distribution.showroom_amount} />}
                      />
                    </div>
                  ) : null}
                  {summary?.profit_summary ? (
                    <div className="min-w-0 rounded-lg border border-border/40 bg-muted/10 px-3 py-0.5 md:col-span-2 xl:col-span-1">
                      <p className="py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Profit summary
                      </p>
                      <SummaryRow
                        label="Purchase cost"
                        value={<MoneyAmount value={summary.profit_summary.purchase_cost} />}
                        muted
                      />
                      <SummaryRow
                        label="Additional Costs"
                        value={
                          <MoneyAmount
                            value={
                              summary.profit_summary.additional_costs ?? summary.additional_costs ?? 0
                            }
                          />
                        }
                        muted
                      />
                      <SummaryRow
                        label="Owner actual profit"
                        value={<MoneyAmount value={summary.profit_summary.owner_actual_profit} emphasis />}
                      />
                      <SummaryRow
                        label="Showroom profit"
                        value={<MoneyAmount value={summary.profit_summary.showroom_profit} emphasis />}
                      />
                      <SummaryRow
                        label="Total profit"
                        value={<MoneyAmount value={summary.profit_summary.total_profit} emphasis />}
                        borderTop
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                {/* 1. Showroom share */}
                <div className="lg:col-span-3 rounded-2xl border border-border/60 bg-muted/10 p-4 flex flex-col justify-between min-h-[220px]">
                  <div>
                    <div className="flex items-center gap-1.5 px-0.5">
                      <Store className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        SHOWROOM SHARE
                      </p>
                    </div>
                    {summary?.showroom ? (
                      <div className="mt-3 space-y-1.5">
                        <p className="text-sm font-bold text-foreground">{summary.showroom.name}</p>
                        <p className="text-xs text-muted-foreground">Dealership Commission & Retainer</p>
                        {summary?.investor_surplus != null && Number(summary.investor_surplus) > 0.009 ? (
                          <div className="mt-2 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] text-emerald-800 dark:text-emerald-300">
                            <span className="font-semibold block">
                              Includes {currSymbol} {formatAmount(Number(summary.investor_surplus))} investor surplus
                            </span>
                            <span className="text-[10px] text-emerald-700/80 dark:text-emerald-300/80">
                              Investor funding exceeded supplier cost
                            </span>
                          </div>
                        ) : null}
                        {summary?.extra_profit != null && Number(summary.extra_profit) > 0.009 ? (
                          <button
                            type="button"
                            onClick={() => setExtraProfitDetailsOpen(true)}
                            className="mt-2 w-full rounded-lg border border-amber-500/20 bg-amber-500/10 px-2.5 py-1.5 text-left text-[11px] text-amber-900 transition-colors hover:bg-amber-500/15 dark:text-amber-300"
                          >
                            <span className="flex items-start justify-between gap-2">
                              <span className="min-w-0">
                                <span className="font-semibold block">
                                  Includes {currSymbol} {formatAmount(Number(summary.extra_profit))} extra profit
                                </span>
                                <span className="text-[10px] text-amber-800/80 dark:text-amber-300/80">
                                  From cost recoveries above actual cost — view details
                                </span>
                              </span>
                              <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-70" />
                            </span>
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-muted-foreground">No showroom share</p>
                    )}
                  </div>

                  <div className="mt-6 flex items-baseline justify-between border-t border-border/30 pt-3">
                    <span className="text-xs font-semibold text-muted-foreground">
                      {formatRatio(summary?.showroom?.percent)}
                    </span>
                    <span className="text-base font-extrabold text-foreground tabular-nums">
                      {currSymbol}{" "}
                      {formatAmount(
                        Number(recipientShareAmount(summary?.showroom ?? {}, summary) ?? 0) +
                          Number(summary?.extra_profit ?? 0),
                      )}
                    </span>
                  </div>
                </div>

                {/* 2. Partner allocations */}
                <div className="lg:col-span-6 rounded-2xl border border-border/60 bg-muted/10 p-4 flex flex-col justify-between min-h-[220px]">
                  <div>
                    <div className="flex items-center justify-between gap-1.5 px-0.5 pb-2">
                      <div className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 shrink-0 text-primary" />
                        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                          PARTNER ALLOCATIONS ({summaryPartners.length})
                        </p>
                      </div>
                      {summary?.distribution_total != null ? (
                        <p className="text-[11px] font-bold text-muted-foreground tracking-tight">
                          {distributionTotalValue < -0.009 ? "LOSS" : "POOL"}: {currSymbol} {formatAmount(Math.abs(distributionTotalValue))}
                        </p>
                      ) : null}
                    </div>

                    {summaryPartners.length > 0 ? (
                      <div className="space-y-2.5">
                        {summaryPartners.map((row, idx) => {
                          const dbDist = distributions.find(
                            (d) => d.partner_id === row.partner_id
                          );
                          const payable = row.final_payable ?? dbDist?.final_payable;
                          const paid = dbDist?.paid_amount ?? (row as any).paid_amount;
                          const balance = Number(payable ?? 0) - Number(paid ?? 0);
                          const amount = recipientShareAmount(row, summary);

                          return (
                            <div
                              key={row.partner_id ?? row.name}
                              className="rounded-xl border border-border/50 bg-background/90 p-3 shadow-2xs"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-2.5 min-w-0">
                                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-[10px] font-bold text-blue-600 dark:text-blue-400 mt-0.5">
                                    {idx + 1}
                                  </span>
                                  <div className="min-w-0">
                                    <p className="truncate text-sm font-bold text-foreground">{row.name}</p>
                                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                                      {row.note ?? (row.commission_type ? `${row.commission_type}` : idx === 0 ? "Primary Vehicle Investor" : "Co-Investor")}
                                    </p>
                                  </div>
                                </div>
                                <div className="shrink-0 text-right">
                                  <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 tabular-nums">
                                    {formatRatio(row.profit_ratio ?? row.percent ?? row.purchase_ratio)} share
                                  </p>
                                  <p className={cn(
                                    "text-sm font-extrabold tabular-nums",
                                    Number(amount ?? 0) < -0.009
                                      ? "text-rose-600 dark:text-rose-400"
                                      : "text-foreground",
                                  )}>
                                    {currSymbol} {formatAmount(Number(amount ?? 0))}
                                  </p>
                                </div>
                              </div>

                              <div className="mt-2.5 grid grid-cols-3 gap-2 border-t border-border/30 pt-2 text-[10px]">
                                <div>
                                  <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">PAYABLE</span>
                                  <span className="font-bold text-foreground text-xs tabular-nums">
                                    {currSymbol} {formatAmount(Number(payable ?? 0))}
                                  </span>
                                </div>
                                <div>
                                  <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">PAID</span>
                                  <span className="font-bold text-emerald-600 dark:text-emerald-400 text-xs tabular-nums">
                                    {currSymbol} {formatAmount(Number(paid ?? 0))}
                                  </span>
                                </div>
                                <div>
                                  <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">BALANCE</span>
                                  <span className="font-bold text-foreground text-xs tabular-nums">
                                    {currSymbol} {formatAmount(Number(balance ?? 0))}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="rounded-lg border border-dashed border-border/50 px-3 py-2 text-xs text-muted-foreground">
                        No deal partners
                      </p>
                    )}
                  </div>                  
                </div>
                {/* 3. Deal Totals */}
                <div className="lg:col-span-3 rounded-2xl border border-border/60 bg-muted/10 p-4 flex flex-col justify-between min-h-[220px]">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">
                      DEAL TOTALS
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="text-muted-foreground">Sale price</span>
                        <span className="font-extrabold text-foreground tabular-nums">
                          {currSymbol} {formatAmount(Number(summary?.sale_price ?? sale.total_amount))}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="text-muted-foreground">Purchase cost</span>
                        <span className="font-bold text-foreground tabular-nums">
                          {currSymbol} {formatAmount(Number(summary?.purchase_cost ?? sale.product?.purchase_price ?? 0))}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="text-muted-foreground">Additional costs</span>
                        <span className="font-bold text-foreground tabular-nums">
                          {currSymbol} {formatAmount(Number(summary?.additional_costs ?? 0))}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs sm:text-sm border-t border-border/30 pt-2">
                        <span className="font-semibold text-foreground">
                          {netProfitValue < -0.009 ? "Net loss" : netProfitValue > 0.009 ? "Net profit" : "Net result"}
                        </span>
                        <span className={cn(
                          "font-extrabold tabular-nums",
                          netProfitValue < -0.009
                            ? "text-rose-600 dark:text-rose-400"
                            : netProfitValue > 0.009
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-foreground",
                        )}>
                          {currSymbol} {formatAmount(netProfitValue)}
                        </span>
                      </div>
                      {summary?.investor_surplus != null && Number(summary.investor_surplus) > 0.009 ? (
                        <div className="flex items-center justify-between text-xs sm:text-sm">
                          <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                            + Investor surplus
                          </span>
                          <span className="font-bold text-emerald-700 dark:text-emerald-400 tabular-nums">
                            {currSymbol} {formatAmount(Number(summary.investor_surplus))}
                          </span>
                        </div>
                      ) : null}
                      {summary?.extra_profit != null && Number(summary.extra_profit) > 0.009 ? (
                        <button
                          type="button"
                          onClick={() => setExtraProfitDetailsOpen(true)}
                          className="flex w-full items-center justify-between gap-2 text-left text-xs sm:text-sm rounded-md -mx-1 px-1 py-0.5 hover:bg-amber-500/10 transition-colors"
                        >
                          <span className="text-amber-800 dark:text-amber-400 font-medium">
                            + Extra profit
                            <span className="ml-1 text-[10px] font-normal text-amber-700/80 dark:text-amber-300/70">
                              (cost recoveries)
                            </span>
                          </span>
                          <span className="inline-flex items-center gap-1 font-bold text-amber-800 dark:text-amber-400 tabular-nums">
                            {currSymbol} {formatAmount(Number(summary.extra_profit))}
                            <ChevronRight className="h-3.5 w-3.5 opacity-70" />
                          </span>
                        </button>
                      ) : null}
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="font-semibold text-foreground">Final distribution</span>
                        <span className="font-extrabold text-foreground tabular-nums">
                          {currSymbol} {formatAmount(Number(summary?.distribution_total ?? summary?.net_profit ?? 0))}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-border/30 flex justify-end">
                    <span className="text-[11px] font-medium text-muted-foreground">
                      {netProfitValue < -0.009
                        ? Math.abs(distributionTotalValue - netProfitValue) < 0.01
                          ? "Loss fully allocated"
                          : "Loss not fully allocated"
                        : netProfitValue > 0.009
                          ? "100% Fully Distributed"
                          : "Break-even"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </SectionCard>

        {/* ── 5. INSTALLMENT SCHEDULE ──────────────────────────────────── */}
        {sale.installments && sale.installments.length > 0 ? (
          <SectionCard
            title="Installment Schedule"
            icon={Wallet}
            action={
              installmentOutstanding != null && installmentOutstanding > 0 ? (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                    Remaining Outstanding
                  </p>
                  <MoneyAmount value={installmentOutstanding} emphasis />
                </div>
              ) : (
                <span className="inline-flex rounded-full bg-emerald-500/15 border border-emerald-500/30 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-400">
                  Fully Paid
                </span>
              )
            }
          >
            <p className="-mt-1 mb-3 text-xs text-muted-foreground">
              {installmentStats
                ? `${installmentStats.paidCount} of ${installmentStats.total} paid · ${sale.installment_count ?? sale.installments.length} total installments`
                : `${sale.installment_count ?? sale.installments.length} total installments`}
            </p>

            {canRecordPayment && !isCancelled && (sale.down_payment_outstanding ?? 0) > 0 ? (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <HandCoins className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-foreground">
                      Down Payment Pending: {formatCurrency(sale.down_payment_outstanding ?? 0)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Booking token ({formatCurrency(sale.down_payment_paid ?? sale.token_amount ?? 0)}) was paid against the agreed down payment of {formatCurrency(sale.down_payment ?? 0)}.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="premium"
                  size="sm"
                  className="h-8 text-xs font-semibold gap-1.5 shrink-0"
                  onClick={() => setDownPaymentDialogOpen(true)}
                >
                  <Wallet className="h-3.5 w-3.5" />
                  Pay Remaining Down Payment
                </Button>
              </div>
            ) : null}

            <div className="-mx-4 overflow-x-auto sm:-mx-5 rounded-xl border border-border/40 bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="border-b border-border/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5 text-left w-12">#</th>
                    <th className="px-4 py-2.5 text-left">Due date</th>
                    <th className="px-4 py-2.5 text-left">Payment date</th>
                    <th className="px-4 py-2.5 text-right">Amount</th>
                    <th className="px-4 py-2.5 text-right">Paid</th>
                    <th className="px-4 py-2.5 text-right">Remaining</th>
                    <th className="px-4 py-2.5 text-center">Status</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/20">
                  {sale.installments.map((row: SaleInstallmentRecord) => {
                    const due = parseISO(row.due_date);
                    const paidDate = row.paid_at && isValid(parseISO(row.paid_at)) ? parseISO(row.paid_at) : null;
                    const remaining = installmentRemaining(row);
                    const canPay = row.status !== "paid";
                    return (
                      <tr key={row.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 tabular-nums text-xs font-medium text-muted-foreground">{row.installment_no}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-xs font-semibold text-foreground">
                          {isValid(due) ? format(due, "PP") : row.due_date}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-xs text-foreground">
                          {paidDate ? (
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                              {format(paidDate, "PP")}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <MoneyAmount value={row.amount} />
                          {(row.penalty_amount ?? 0) > 0.009 ? (
                            <p className="text-[10px] text-rose-600 dark:text-rose-400 mt-0.5">
                              + Penalty {Number(row.penalty_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <MoneyAmount
                            value={Number(row.paid_amount || 0) + Number(row.penalty_paid || 0)}
                            amountClassName="font-normal text-muted-foreground"
                          />
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <MoneyAmount value={remaining} emphasis={remaining > 0} />
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wide border",
                              row.status === "paid"
                                ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                                : row.status === "overdue"
                                  ? "border-rose-500/30 bg-rose-500/15 text-rose-700 dark:text-rose-400"
                                  : "border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-400",
                            )}
                          >
                            {installmentStatusLabel(row.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {canRecordPayment && !isCancelled && canPay ? (
                            <Button
                              type="button"
                              variant="premium"
                              size="sm"
                              className="h-7 text-xs font-semibold px-2.5"
                              onClick={() => setPaymentTarget(row)}
                            >
                              Record Payment
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground font-medium">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>
        ) : null}

        {/* ── 6. PAYMENT & INSTALLMENT LEDGER ──────────────────────────── */}
        {sale.payments && sale.payments.length > 0 ? (
          <div className="glass-card overflow-hidden p-5 sm:p-6 space-y-5">
            {/* Header: Title + Subtitle on left, Total Collected + Record Payment on right */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-blue-500/20 bg-blue-500/10 text-blue-600 dark:text-blue-400 mt-0.5">
                  <History className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h2 className="text-base font-bold text-foreground sm:text-lg">
                      Payment &amp; Installment Ledger
                    </h2>
                    <span className="rounded-full border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                      {sale.payments.length} {sale.payments.length === 1 ? "Entry" : "Entries"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Real-time audit log of booking token and subsequent balance payments
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 sm:justify-end">
                <div className="text-left sm:text-right">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">TOTAL COLLECTED</p>
                  <div className="flex items-baseline gap-1.5 sm:justify-end">
                    <span className="text-lg font-extrabold text-foreground tabular-nums">
                      {currSymbol} {formatAmount(paidTotalAmount)}
                    </span>
                    <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">({percentPaid}%)</span>
                  </div>
                </div>
                {canRecordPayment && !isCancelled && !sale.is_installment && (sale.outstanding ?? sale.balance_outstanding ?? 0) > 0 ? (
                  <Button
                    type="button"
                    size="sm"
                    className="h-9 gap-1.5 text-xs font-bold px-4 bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white shadow-xs rounded-lg"
                    onClick={() => setBalanceDialogOpen(true)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Record Payment
                  </Button>
                ) : null}
              </div>
            </div>

            {/* Search & Filter Bar */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-1">
              {/* Search input */}
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search memo, bank, receipt #..."
                  value={ledgerSearch}
                  onChange={(e) => setLedgerSearch(e.target.value)}
                  className="h-9 w-full rounded-lg border border-border/70 bg-muted/20 pl-9 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40 transition-colors"
                />
              </div>

              {/* Method filter pills */}
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mr-1">METHOD:</span>
                <button
                  type="button"
                  onClick={() => setLedgerMethodFilter("all")}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-xs font-bold transition-colors",
                    ledgerMethodFilter === "all"
                      ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-xs"
                      : "bg-background text-muted-foreground hover:bg-muted hover:text-foreground border border-border/60",
                  )}
                >
                  All
                </button>
                {paymentMethods.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setLedgerMethodFilter(m)}
                    className={cn(
                      "rounded-md px-3 py-1.5 text-xs font-semibold transition-colors capitalize",
                      ledgerMethodFilter === m
                        ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-xs"
                        : "bg-background text-muted-foreground hover:bg-muted hover:text-foreground border border-border/60",
                    )}
                  >
                    {paymentMethodLabel(m)}
                  </button>
                ))}
              </div>
            </div>

            {/* Seamless Table */}
            <div className="-mx-5 overflow-x-auto sm:-mx-6 border-t border-border/40 pt-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <th className="px-5 py-3 text-left sm:px-6">Date / Time</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Method &amp; Channel</th>
                    <th className="px-4 py-3 text-left">Reference / Memo</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-5 py-3 text-right sm:px-6">Receipt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/25">
                  {filteredPayments.map((row: SalePaymentRecord, idx: number) => {
                    const date = row.payment_date ? parseISO(row.payment_date) : null;
                    const createdAt = row.created_at ? parseISO(row.created_at) : null;
                    const reference = [row.bank_name, row.transaction_reference_no, row.pay_order_no, row.notes]
                      .filter((v) => v && String(v).trim())
                      .join(" • ");

                    const originalIdx = sale.payments!.indexOf(row);
                    const receiptId = makeReceiptId(invoiceNo, originalIdx >= 0 ? originalIdx : idx);
                    const methodBadge = paymentMethodBadge(row.payment_method);

                    const txType = row.transaction_type_label ?? row.transaction_type ?? "Payment";
                    const isToken = (row.transaction_type ?? "").toLowerCase().includes("token") || txType.toLowerCase().includes("token");

                    return (
                      <tr key={row.id} className="hover:bg-muted/15 transition-colors">
                        {/* Date / Time */}
                        <td className="px-4 py-2 whitespace-nowrap sm:px-4">
                          <p className="text-xs font-bold text-foreground">
                            {date && isValid(date) ? format(date, "MMM d, yyyy") : (row.payment_date ?? "—")}
                          </p>
                          <p className="text-[10px] font-medium text-muted-foreground mt-0.5">
                            {createdAt && isValid(createdAt) ? format(createdAt, "hh:mm a") : ""}
                            {" "}PKT
                          </p>
                        </td>

                        {/* Type */}
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className={cn(
                            "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold border",
                            isToken
                              ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/60"
                              : "bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
                          )}>
                            {isToken ? <ShieldCheck className="h-3 w-3 text-indigo-600 dark:text-indigo-400" /> : null}
                            {isToken ? "Token Deposit" : txType}
                          </span>
                        </td>

                        {/* Method & Channel */}
                        <td className="px-4 py-2">
                          <span className={cn(
                            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold",
                            methodBadge.className,
                          )}>
                            {methodBadge.emoji} {methodBadge.label}
                          </span>
                          {row.bank_name ? (
                            <p className="mt-1 text-[10px] text-muted-foreground truncate max-w-[200px]">
                              Bank: {row.bank_name}
                            </p>
                          ) : null}
                        </td>

                        {/* Reference / Memo */}
                        <td className="px-4 py-2 max-w-[320px]">
                          <p className="text-xs text-foreground/90 font-medium" title={reference || undefined}>
                            {reference || "—"}
                          </p>
                        </td>

                        {/* Amount */}
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <span className="text-sm font-extrabold text-foreground tabular-nums">
                            {formatCurrency(Number(row.amount))}
                          </span>
                        </td>

                        {/* Receipt */}
                        <td className="px-4 py-2 text-right whitespace-nowrap sm:px-4">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 gap-1.5 px-2.5 text-[11px] font-mono font-semibold text-blue-700 dark:text-blue-400 bg-blue-50/70 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900/60 hover:bg-blue-100 hover:text-blue-800 transition-colors shadow-2xs"
                            onClick={() => {
                              setSelectedReceiptPayment(row);
                              setSelectedReceiptId(receiptId);
                              setReceiptDialogOpen(true);
                            }}
                            title="View & Print Payment Receipt"
                          >
                            <Printer className="h-3 w-3" />
                            {receiptId}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-xs text-muted-foreground">
                        No payments match your filter.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

      </div>

      {/* ── CANCELLED BANNER ──────────────────────────────────────────── */}
      {isCancelled ? (
        <div
          className={cn(
            "mt-4 rounded-xl border px-4 py-3 text-sm",
            isReturned
              ? "border-violet-500/30 bg-violet-500/10 text-violet-900 dark:text-violet-200"
              : "border-rose-500/30 bg-rose-500/10 text-rose-800 dark:text-rose-200",
          )}
        >
          {isReturned
            ? "This sale was returned. Customer collections were refunded, sale books were reversed, and the vehicle was restored to stock."
            : "This sale is cancelled. Customer collections were refunded, sale books were reversed, and the vehicle was returned to stock."}
          {sale.cancel_notes ? <span className="mt-1 block text-xs opacity-90">{sale.cancel_notes}</span> : null}
        </div>
      ) : null}

      {/* ── DIALOGS ───────────────────────────────────────────────────── */}
      <AlertDialog open={cancelOpen} onOpenChange={(open) => !cancelSaving && setCancelOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this sale?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Customer walk-away will refund all money received on this sale, reverse sale accounting, and put the vehicle back in stock.
              </span>
              <span className="block text-foreground/80">
                Refund amount (customer paid):{" "}
                <strong>{formatCurrency(Number(sale.paid_total ?? 0))}</strong>
              </span>
              <span className="block text-xs text-muted-foreground">
                Owner/partner payouts already made are not auto-reversed.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelSaving}>Keep sale</AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelSaving}
              className="bg-rose-600 text-white hover:bg-rose-700"
              onClick={(e) => {
                e.preventDefault();
                void submitCancelSale();
              }}
            >
              {cancelSaving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Cancel sale &amp; refund
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={extraProfitDetailsOpen} onOpenChange={setExtraProfitDetailsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Extra profit from cost recoveries</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">
            Amounts received above the scheduled cost line. This profit goes to the showroom / company.
          </p>
          {(summary?.extra_profit_details?.length ?? 0) > 0 ? (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-0.5">
              {(summary?.extra_profit_details ?? []).map((row) => (
                <div
                  key={row.id}
                  className="rounded-xl border border-border/50 bg-muted/15 px-3 py-2.5 space-y-1.5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{row.cost_label}</p>
                      {row.party_name ? (
                        <p className="text-[11px] text-muted-foreground truncate">From {row.party_name}</p>
                      ) : null}
                      {row.payment_date ? (
                        <p className="text-[10px] text-muted-foreground">{row.payment_date}</p>
                      ) : null}
                    </div>
                    <p className="shrink-0 text-sm font-extrabold text-amber-800 dark:text-amber-400 tabular-nums">
                      +{currSymbol} {formatAmount(Number(row.profit_amount))}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 border-t border-border/40 pt-1.5 text-[10px]">
                    <div>
                      <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                        Cost
                      </span>
                      <span className="font-bold text-foreground tabular-nums">
                        {currSymbol} {formatAmount(Number(row.actual_amount))}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                        Received
                      </span>
                      <span className="font-bold text-foreground tabular-nums">
                        {currSymbol} {formatAmount(Number(row.received_amount))}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                        Extra
                      </span>
                      <span className="font-bold text-amber-800 dark:text-amber-400 tabular-nums">
                        {currSymbol} {formatAmount(Number(row.profit_amount))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-border/50 px-3 py-6 text-center text-xs text-muted-foreground">
              No cost-line recovery details found.
            </p>
          )}
          <div className="flex items-center justify-between border-t border-border/40 pt-3 text-sm">
            <span className="font-semibold text-foreground">Total extra profit</span>
            <span className="font-extrabold text-amber-800 dark:text-amber-400 tabular-nums">
              {currSymbol} {formatAmount(Number(summary?.extra_profit ?? 0))}
            </span>
          </div>
        </DialogContent>
      </Dialog>

      <GenerateGatePassDialog
        open={gatePassOpen}
        onOpenChange={setGatePassOpen}
        date={gatePassDate}
        time={gatePassTime}
        onDateChange={setGatePassDate}
        onTimeChange={setGatePassTime}
        saving={gatePassSaving}
        onSubmit={() => void submitGatePass()}
        checklistAttributes={checklistAttributes}
        checklistValues={checklistValues}
        onChecklistValueChange={(code, val) => {
          setChecklistValues((prev) => ({
            ...prev,
            [code]: val,
          }));
        }}
      />

      <RecordBalancePaymentDialog
        open={balanceDialogOpen}
        onOpenChange={setBalanceDialogOpen}
        outstanding={sale.outstanding ?? sale.balance_outstanding ?? 0}
        onSubmit={async (payload) => {
          try {
            const r = await paySaleBalance(sale.id, payload);
            toast.success(r.message);
            if (r.record) {
              setSale(r.record);
            }
            await reload();
          } catch (e) {
            toast.error(saleInvoiceErrorMessage(e));
            throw e;
          }
        }}
      />

      <RecordBalancePaymentDialog
        open={downPaymentDialogOpen}
        onOpenChange={setDownPaymentDialogOpen}
        outstanding={sale.down_payment_outstanding ?? 0}
        title="Record Down Payment"
        description="Record the remaining down payment for this installment sale."
        balanceLabel="Remaining Down Payment"
        defaultNoteText="Installment Down Payment"
        onSubmit={async (payload) => {
          try {
            const r = await paySaleBalance(sale.id, payload);
            toast.success(r.message);
            if (r.record) {
              setSale(r.record);
            }
            await reload();
          } catch (e) {
            toast.error(saleInvoiceErrorMessage(e));
            throw e;
          }
        }}
      />

      <RecordInstallmentPaymentDialog
        open={paymentTarget != null}
        onOpenChange={(open) => !open && setPaymentTarget(null)}
        installment={paymentTarget}
        saleOutstanding={installmentOutstanding}
        onSubmit={async (payload) => {
          if (!paymentTarget) return;
          try {
            const r = await paySaleInstallment(sale.id, paymentTarget.id, payload);
            toast.success(r.message);
            if (r.record) {
              setSale(r.record);
            }
            await reload();
            setPaymentTarget(null);
          } catch (e) {
            toast.error(saleInvoiceErrorMessage(e));
            throw e;
          }
        }}
      />

      {sale && (
        <PaymentReceiptDialog
          open={receiptDialogOpen}
          onOpenChange={setReceiptDialogOpen}
          sale={sale}
          payment={selectedReceiptPayment}
          receiptId={selectedReceiptId}
        />
      )}
    </Layout>
  );
};

export default SaleInvoice;
